import type { Lead, LeadStatus } from './types'

/** Map backend lead status values to frontend LeadStatus values. */
const STATUS_MAP: Record<string, LeadStatus> = {
  discovered: 'new',
  warmup: 'warmup_done',
  // 'contacted' means the first DM went out (post-accept), NOT that an
  // invite was sent — mapping it to 'connection_sent' (a V2 leftover) sent
  // messaged leads BACKWARDS to the "Request Sent" column. It has its own
  // kanban column ("Messaged"), as do the follow-up stages; unmapped
  // statuses fall back to 'new', which dumped every followup_* lead into
  // "Discovered".
  contacted: 'contacted',
  connection_sent: 'connection_sent',
  connected: 'connected',
  dm_sent: 'dm_sent',
  followup_1_sent: 'followup_1_sent',
  followup_2_sent: 'followup_2_sent',
  followup_3_sent: 'followup_3_sent',
  // Pro runs follow-ups 4 and 5, Custom up to 7 (card P1); older sequences
  // also reached 4. Without these entries those leads fell back to 'new'.
  followup_4_sent: 'followup_4_sent',
  followup_5_sent: 'followup_5_sent',
  followup_6_sent: 'followup_6_sent',
  followup_7_sent: 'followup_7_sent',
  replied: 'replied',
  qualified: 'qualified',
  meeting_booked: 'meeting_booked',
  won: 'won',
  lost: 'lost',
  new: 'new',
  warmup_done: 'warmup_done',
}

function mapStatus(raw: string): LeadStatus {
  return STATUS_MAP[raw] ?? 'new'
}

/**
 * Backend lead shape as returned by the leads worker.
 * Field names differ slightly from the frontend Lead type.
 */
export type BackendLead = {
  readonly id: string
  readonly linkedin_url: string | null
  readonly first_name: string | null
  readonly last_name: string | null
  readonly full_name: string | null
  readonly headline: string | null
  readonly company: string | null
  readonly title: string | null       // frontend uses job_title
  readonly industry: string | null
  readonly location: string | null
  readonly avatar_url: string | null  // frontend uses photo_url
  readonly status: string
  readonly icp_score: number
  readonly intent_score: number
  readonly composite_score: number
  readonly enriched_at: string | null
  readonly last_activity_at: string | null
  readonly created_at: string
  readonly updated_at: string
  readonly campaign_id: string | null
  readonly campaign_name?: string | null  // joined server-side from campaigns.name
  /** Lead refresh (migration 0054): summary of their LinkedIn profile and when it was read. */
  readonly profile_summary?: string | null
  readonly profile_refreshed_at?: string | null
}

/**
 * Normalize a backend lead record to the frontend Lead shape.
 * Maps `title` → `job_title` and `avatar_url` → `photo_url`.
 */
export function normalizeLead(raw: BackendLead): Lead {
  return {
    id: raw.id,
    linkedin_url: raw.linkedin_url ?? '',
    first_name: raw.first_name ?? '',
    last_name: raw.last_name ?? '',
    full_name: raw.full_name ?? undefined,
    headline: raw.headline ?? undefined,
    company: raw.company ?? undefined,
    job_title: raw.title ?? undefined,
    location: raw.location ?? undefined,
    photo_url: raw.avatar_url ?? undefined,
    campaign_id: raw.campaign_id ?? undefined,
    campaign: raw.campaign_name ?? undefined,
    status: mapStatus(raw.status ?? ''),
    icp_score: raw.icp_score,
    intent_score: raw.intent_score,
    composite_score: raw.composite_score,
    industry: raw.industry ?? undefined,
    enriched_at: raw.enriched_at ?? undefined,
    last_activity_at: raw.last_activity_at ?? undefined,
    created_at: raw.created_at,
    profile_summary: raw.profile_summary ?? undefined,
    profile_refreshed_at: raw.profile_refreshed_at ?? undefined,
  }
}
